/* eslint-disable react-hooks/exhaustive-deps */
import { Box, FormControl, InputLabel, MenuItem, Paper, Select, Stack } from '@mui/material';
import { SectionWithHeading } from '../../../../shared/components/common/sectionWithHeading';
import { TextWrapper } from '../../../../shared/components/formUtilities/useTextWrapper';
import { CURRENCY, IS_SA } from '../../../../config/appConfig';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { useEffect } from 'react';
import { BorrowerService } from '../../borrowerService';
import { InvestorService } from '../../../investor/investorService';
import { LoanService } from '../../../../services/loanService';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FSBankDetails } from '../../../../shared/components/fsBankDetails';
import { LocalStorageKey } from '../../../../constants/localStorageKeys';
import { trackOpenPanelView } from '../../../../helpers/openPanelHelper';
import { trackCustomerIoView } from '../../../../helpers/customerIoHelper';
import { OpenPanelEvents, OpenPanelCategories } from '../../../../constants/openPanelEvents';
import { DateHelper } from '../../../../helpers/dateHelper';
import { currencyFormatterHelper } from '../../../../helpers/currencyFormatterHelper';

const DetailRow = ({ label, value }) => (
	<Box className="grid grid-cols-[130px_1fr] items-center gap-x-2">
		<TextWrapper classList="!caption !text-sm" text={label}></TextWrapper>
		<TextWrapper classList="!font-bold !text-sm" text={value}></TextWrapper>
	</Box>
);

export default function BankTransfer() {
	const borrowerService = new BorrowerService();
	const investorService = new InvestorService();
	const loanService = new LoanService();
	const [userBankDetails, setUserBankDetails] = useState();
	const [activeLoans, setActiveLoans] = useState([]);
	const [virtualIban, setVirtualIban] = useState();
	const [loanId, setLoanId] = useState('');
	const [amount, setAmount] = useState('');
	const { t } = useTranslation();

	useEffect(() => {
		trackOpenPanelView(OpenPanelEvents.depositRequestViewed, { category: OpenPanelCategories.checkout });
		trackCustomerIoView(OpenPanelEvents.depositRequestViewed, { category: OpenPanelCategories.checkout });
	}, []);

	useEffect(() => {
		getUserBankDetails();
		getActiveLoans();
	}, []);

	const getActiveLoans = () => {
		borrowerService
			.getActiveLoans()
			.then((resp) => {
				if (resp.data && resp.data.length > 0) {
					setActiveLoans(resp.data);
					const storedLoanId = sessionStorage.getItem(LocalStorageKey.activeLoanId);
					const initialLoanId = storedLoanId && resp.data.some(loan => loan.loanId === storedLoanId)
						? storedLoanId
						: resp.data[0].loanId;
					setLoanId(initialLoanId);
					getSummary(initialLoanId);
					getVirtualIban(initialLoanId);
				}
			})
			.catch((err) => { });
	};

	const handleLoanChange = (event) => {
		const nextLoanId = event.target.value;
		setLoanId(nextLoanId);
		getSummary(nextLoanId);
		getVirtualIban(nextLoanId);
	};

	const getUserBankDetails = () => {
		investorService
			.getBankDetails()
			.then((resp) => {
				setUserBankDetails(resp.data);
			})
			.catch((err) => { });
	};

	const getSummary = (loanId) => {
		borrowerService
			.getSummary(loanId)
			.then((resp) => {
				const summary = resp.data;
				const payments = summary?.payments?.filter((s) => !s.isPaid) ?? [];
				setAmount(payments.length !== 0 ? payments[0].amount : '');
			})
			.catch((err) => { });
	};

	const getVirtualIban = (loanId) => {
		loanService
			.getVirtualIban(loanId)
			.then((resp) => {
				setVirtualIban(resp.data);
			})
			.catch((err) => { });
	};

	return (
		<Box>
			<TextWrapper
				classList="!text-[22px] !font-[700] !text-start"
				text={t('depositMoney')}></TextWrapper>
			<Box className="grid md:grid-cols-2 grid-cols-1 gap-4 mt-4">
				<SectionWithHeading
					hideHeader
					children={
						<Stack gap={4}>
							{activeLoans.length > 1 && (
								<Stack gap={4}>
									<TextWrapper
										classList="caption !text-sm"
										text={t('borrowerBankTransferInstructions')}></TextWrapper>
									<FormControl fullWidth size="small" className="md:!w-1/2">
										<InputLabel id="loan-select-label">{t('selectLoanIdStartDate')}</InputLabel>
										<Select
											labelId="loan-select-label"
											label={t('selectLoanIdStartDate')}
											value={loanId}
											onChange={handleLoanChange}
										>
											{activeLoans.map((loan) => {
												const formattedStartDate = DateHelper.formatDate(loan.startDate, 'dd MMM yyyy');
												return (
													<MenuItem key={loan.loanId} value={loan.loanId}>
														{`Loan ${loan.applicationIdPadded}${formattedStartDate ? ` - ${formattedStartDate}` : ''}`}
													</MenuItem>
												);
											})}
										</Select>
									</FormControl>
									{amount !== '' ? (
										<DetailRow
											label={t('amount')}
											value={`${CURRENCY} ${currencyFormatterHelper(amount)}`}
										/>
									) : null}
								</Stack>
							)}
							<Stack gap={2}>
								<TextWrapper
									classList="!text-lg !font-[700]"
									text={t('yourDepositReference')}></TextWrapper>
								<DetailRow label={t('reference')} value={userBankDetails?.reference} />
								<DetailRow label={t('bankName')} value={userBankDetails?.bankName} />
								<DetailRow label={t('accountName')} value={userBankDetails?.accountName} />
							</Stack>
							<FSBankDetails virtualIban={virtualIban} compact></FSBankDetails>
						</Stack>
					}></SectionWithHeading>
				<SectionWithHeading
					classList="!h-auto"
					bodyClass="!h-full"
					heading={
						<Box className="flex items-center">
							<span className="mx-2">{t('importantInfo')}</span>
						</Box>
					}
					children={
						<Box className="flex flex-col flex-0 justify-between h-full">
							<Stack gap={4}>
								<Box>
									<TextWrapper
										classList="caption !text-sm"
										text={t(
											'depositMoneyIntoFundinSouqAccountText'
										)}></TextWrapper>
								</Box>
								<Stack gap={2}>
									<Box className="flex flex-0 items-start">
										<CheckCircleOutlineOutlinedIcon
											className="!text-[20px]"
											color="primary"></CheckCircleOutlineOutlinedIcon>
										<TextWrapper
											classList="caption !text-sm !mx-1"
											text={t(
												'depositMoneyIntoFundinSouqAccountText1'
											)}></TextWrapper>
									</Box>
									<Box className="flex flex-0 items-start">
										<CheckCircleOutlineOutlinedIcon
											className="!text-[20px]"
											color="primary"></CheckCircleOutlineOutlinedIcon>
										<TextWrapper
											classList="caption !text-sm !mx-1"
											text={t(
												'depositMoneyIntoFundinSouqAccountText2'
											)}></TextWrapper>
									</Box>
									<Box className="flex flex-0 items-start">
										<CheckCircleOutlineOutlinedIcon
											className="!text-[20px]"
											color="primary"></CheckCircleOutlineOutlinedIcon>
										<TextWrapper
											classList="caption !text-sm !mx-1"
											text={
												<span>
													{t('depositMoneyIntoFundinSouqAccountText3')}
													<a
														className="underline-link mx-1"
														href="mailto:support@fundingsouq.com">
														support@fundingsouq.com
													</a>
												</span>
											}></TextWrapper>
									</Box>
								</Stack>
							</Stack>
							<Box
								component={Paper}
								className="p-3 mt-20 !bg-[#f9f9f9] flex flex-col flex-0">
								<TextWrapper
									classList="!text-md !font-bold"
									text={t('investorFundProtection')}></TextWrapper>
								<TextWrapper
									classList="!text-sm !caption"
									text={
										IS_SA
											? t('yourFundsAreSecurelyANB')
											: t('yourFundsAreSecurely')
									}></TextWrapper>
							</Box>
						</Box>
					}></SectionWithHeading>
			</Box>
		</Box>
	);
}
